import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { IoClose, IoOpenOutline, IoRefresh, IoDownload } from 'react-icons/io5';
import Pagination from '../../../components/common/Pagination';
import Spinner from '../../../components/common/Spinner';
import { db } from '../../../firebase/config';
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { refundTossPayment } from '../../../services/tossPaymentService';
import { useAuth } from '../../../context/AuthContext';

const STATUS_LABEL = {
  completed: { label: '결제완료', color: '#2563EB', bg: '#DBEAFE' },
  DONE: { label: '결제완료', color: '#2563EB', bg: '#DBEAFE' },
  refunded: { label: '환불완료', color: '#DC2626', bg: '#FEE2E2' },
  partial_refunded: { label: '부분환불', color: '#EA580C', bg: '#FFEDD5' },
  pending: { label: '요청중', color: '#F59E0B', bg: '#FEF3C7' },
  cancelled: { label: '취소', color: '#6B7280', bg: '#F3F4F6' },
  failed: { label: '실패', color: '#9CA3AF', bg: '#F3F4F6' },
};

const KIND_LABEL = {
  db_purchase: 'DB 마켓 구매',
  db_purchase_bulk: 'DB 일괄 구매',
  payment_request: '본사 청구 결제',
  receivable_bulk: '미수금 일괄 결제',
};

const PER_PAGE = 20;

const formatDateTime = (ts) => {
  if (!ts) return '-';
  const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}. ${pad(d.getMonth() + 1)}. ${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const maskName = (name) => {
  if (!name || name.length < 2) return name || '-';
  if (name.length === 2) return `${name[0]}*`;
  return `${name[0]}${'*'.repeat(name.length - 2)}${name[name.length - 1]}`;
};

const PaymentHistory = () => {
  const { userData } = useAuth();
  const [payments, setPayments] = useState([]);
  const [partners, setPartners] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'payments'), orderBy('created_at', 'desc'), limit(500)));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPayments(list);

      // 파트너 매핑 (UI 표시용 이름/연락처)
      const partnerIds = Array.from(new Set(list.map((p) => p.partner_id).filter(Boolean)));
      const partnerMap = {};
      await Promise.all(partnerIds.map(async (pid) => {
        try {
          const pDoc = await getDoc(doc(db, 'partners', pid));
          if (pDoc.exists()) partnerMap[pid] = pDoc.data();
          else {
            const uDoc = await getDoc(doc(db, 'users', pid));
            if (uDoc.exists()) partnerMap[pid] = uDoc.data();
          }
        } catch { /* skip */ }
      }));
      setPartners(partnerMap);
    } catch (err) {
      console.error('fetchPayments failed:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    let list = payments;
    if (statusFilter !== 'all') {
      list = list.filter((p) => {
        if (statusFilter === 'completed') return p.status === 'completed' || p.status === 'DONE';
        if (statusFilter === 'refunded') return p.status === 'refunded' || p.status === 'partial_refunded';
        return p.status === statusFilter;
      });
    }
    if (search.trim()) {
      const kw = search.trim().toLowerCase();
      list = list.filter((p) => {
        const partner = partners[p.partner_id] || {};
        const blob = [
          partner.business_name, partner.company_name, partner.contact_phone, partner.phone,
          p.payment_order_id, p.label, p.method,
        ].filter(Boolean).join(' ').toLowerCase();
        return blob.includes(kw);
      });
    }
    return list;
  }, [payments, statusFilter, search, partners]);

  const totalAmount = useMemo(
    () => filtered.filter((p) => p.status === 'completed' || p.status === 'DONE').reduce((s, p) => s + Number(p.amount || 0), 0),
    [filtered]
  );
  const refundAmount = useMemo(
    () => filtered.filter((p) => p.status === 'refunded' || p.status === 'partial_refunded').reduce((s, p) => s + Number(p.refund_amount || p.amount || 0), 0),
    [filtered]
  );
  const completedCount = useMemo(() => filtered.filter((p) => p.status === 'completed' || p.status === 'DONE').length, [filtered]);
  const refundedCount = useMemo(() => filtered.filter((p) => p.status === 'refunded' || p.status === 'partial_refunded').length, [filtered]);
  const pendingCount = useMemo(() => filtered.filter((p) => p.status === 'pending').length, [filtered]);
  const cancelledCount = useMemo(() => filtered.filter((p) => p.status === 'cancelled' || p.status === 'failed').length, [filtered]);

  const pageList = useMemo(() => filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE), [filtered, page]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));

  useEffect(() => { setPage(1); }, [statusFilter, search]);

  const handleSearch = () => setSearch(searchInput);
  const handleDownloadCsv = () => {
    const header = ['결제대상', '업체명', '상품명', '서비스', '결제ID', '결제방법', '금액', '상태', '결제완료시', '환불일시', '환불사유'];
    const rows = filtered.map((p) => {
      const partner = partners[p.partner_id] || {};
      const phone = partner.contact_phone || partner.phone || '-';
      const partnerName = partner.business_name || partner.company_name || partner.name || '-';
      return [
        phone, partnerName, p.label || KIND_LABEL[p.kind] || '-',
        KIND_LABEL[p.kind] || p.kind || '-',
        p.payment_order_id || p.id, p.method || '-',
        Number(p.amount || 0), STATUS_LABEL[p.status]?.label || p.status || '-',
        formatDateTime(p.paid_at || p.created_at),
        formatDateTime(p.refunded_at), p.refund_reason || '-',
      ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',');
    });
    const csv = '﻿' + [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openRefund = (payment) => {
    setRefundTarget(payment);
    setRefundReason('');
  };

  const submitRefund = async () => {
    if (!refundTarget) return;
    if (!refundReason.trim()) {
      alert('환불 사유를 입력해주세요.');
      return;
    }
    if (!refundTarget.payment_key) {
      alert('이 결제건은 paymentKey 가 없어 PG 환불이 불가합니다. (수동 처리 필요)');
      return;
    }
    if (!window.confirm(`정말 ${Number(refundTarget.amount || 0).toLocaleString()}원을 환불하시겠습니까?`)) return;
    setRefundLoading(true);
    try {
      await refundTossPayment({
        paymentKey: refundTarget.payment_key,
        orderId: refundTarget.payment_order_id,
        cancelReason: refundReason,
        adminEmail: userData?.email || null,
      });
      alert('환불 완료');
      setRefundTarget(null);
      await fetchData();
    } catch (err) {
      alert(`환불 실패: ${err.message || err}`);
    } finally {
      setRefundLoading(false);
    }
  };

  return (
    <Wrap>
      <Header>
        <div>
          <Eyebrow>관리자 결제</Eyebrow>
          <Title>결제내역</Title>
          <Subtitle>DB, 미수, 청구, 마일리지 충전 결제를 모아서 조회합니다. 상태는 결제완료, 환불완료, 요청중, 취소 네 가지로 통합합니다.</Subtitle>
        </div>
        <Actions>
          <ToolBtn onClick={fetchData}><IoRefresh /> 새로고침</ToolBtn>
          <ToolBtn onClick={handleDownloadCsv}><IoDownload /> CSV 다운로드</ToolBtn>
        </Actions>
      </Header>

      <KpiRow>
        <KpiCard>
          <KpiLabel>조회 건수</KpiLabel>
          <KpiValue>{filtered.length}건</KpiValue>
          <KpiHint>현재 조회 조건 기준</KpiHint>
        </KpiCard>
        <KpiCard>
          <KpiLabel>총 결제금액</KpiLabel>
          <KpiValue>{totalAmount.toLocaleString()}원</KpiValue>
          <KpiHint>현재 목록 합산 금액</KpiHint>
        </KpiCard>
        <KpiCard>
          <KpiLabel>결제완료 / 환불완료</KpiLabel>
          <KpiValue>{completedCount}원 / {refundAmount.toLocaleString()}원</KpiValue>
          <KpiHint>완료 카드와 환불 합계</KpiHint>
        </KpiCard>
        <KpiCard>
          <KpiLabel>요청중 / 취소</KpiLabel>
          <KpiValue>{pendingCount}원 / {cancelledCount}원</KpiValue>
          <KpiHint>대기·실패 결제 건 합계</KpiHint>
        </KpiCard>
      </KpiRow>

      <FilterBar>
        <SearchBox>
          <span>🔍</span>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="업체명, 고객명, 서비스, 결제 ID 검색"
          />
        </SearchBox>
        <SearchBtn onClick={handleSearch}>조회</SearchBtn>
        <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">전체</option>
          <option value="completed">결제완료</option>
          <option value="refunded">환불완료</option>
          <option value="pending">요청중</option>
          <option value="cancelled">취소</option>
        </FilterSelect>
        <ResultCount>총 {filtered.length}건</ResultCount>
      </FilterBar>

      <TableWrap>
        {loading ? (
          <LoadingWrap><Spinner /></LoadingWrap>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>결제대상</Th>
                <Th>결제 ID</Th>
                <Th>결제방법</Th>
                <ThRightAlign>금액</ThRightAlign>
                <Th>상태</Th>
                <Th>결제일시</Th>
                <Th>환불정보</Th>
                <ThRight>액션</ThRight>
              </tr>
            </thead>
            <tbody>
              {pageList.length === 0 ? (
                <tr><Td colSpan={8}><Empty>조회된 결제가 없습니다.</Empty></Td></tr>
              ) : pageList.map((p) => {
                const partner = partners[p.partner_id] || {};
                const partnerName = partner.business_name || partner.company_name || partner.name || '-';
                const phone = partner.contact_phone || partner.phone;
                const status = STATUS_LABEL[p.status] || { label: p.status || '-', color: '#6B7280', bg: '#F3F4F6' };
                const isRefunded = p.status === 'refunded' || p.status === 'partial_refunded';
                const canRefund = (p.status === 'completed' || p.status === 'DONE') && p.payment_key;
                return (
                  <tr key={p.id}>
                    <Td>
                      <TargetCell>
                        <SmallTag>DB</SmallTag>
                        <div>
                          <strong>{phone || maskName(partnerName)}</strong>
                          <div className="muted">{partnerName}</div>
                          <div className="muted">{p.label || KIND_LABEL[p.kind] || '-'}</div>
                        </div>
                      </TargetCell>
                    </Td>
                    <Td>
                      <OrderId>
                        <strong>{p.payment_order_id || p.id}</strong>
                        <span className="muted">결제 대상 {partnerName}</span>
                      </OrderId>
                    </Td>
                    <Td>{p.method || '카드'}</Td>
                    <TdRightAlign><strong>{Number(p.amount || 0).toLocaleString()}원</strong></TdRightAlign>
                    <Td>
                      <StatusCell>
                        <Badge $color={status.color} $bg={status.bg}>● {status.label}</Badge>
                        {p.payment_key && (
                          <a
                            href={`https://dashboard.tosspayments.com/payments/${encodeURIComponent(p.payment_key)}`}
                            target="_blank" rel="noreferrer"
                            title="토스 대시보드"
                          ><IoOpenOutline /></a>
                        )}
                      </StatusCell>
                    </Td>
                    <Td>{formatDateTime(p.paid_at || p.created_at)}</Td>
                    <Td>
                      {p.refunded_at ? (
                        <RefundCell>
                          <strong>{formatDateTime(p.refunded_at)}</strong>
                          {p.refund_reason && <div className="muted">{p.refund_reason}</div>}
                        </RefundCell>
                      ) : '-'}
                    </Td>
                    <TdRight>
                      {isRefunded ? (
                        <RefundedBadge>● 환불완료</RefundedBadge>
                      ) : canRefund ? (
                        <RefundBtn onClick={() => openRefund(p)}>환불 처리</RefundBtn>
                      ) : null}
                    </TdRight>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </TableWrap>

      {totalPages > 1 && (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {refundTarget && (() => {
        const partner = partners[refundTarget.partner_id] || {};
        const partnerName = partner.business_name || partner.company_name || partner.name || '-';
        const labelText = refundTarget.label || KIND_LABEL[refundTarget.kind] || '-';
        return (
          <ModalBg onClick={() => !refundLoading && setRefundTarget(null)}>
            <Modal onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h3>환불 처리</h3>
                <CloseBtn onClick={() => !refundLoading && setRefundTarget(null)}><IoClose /></CloseBtn>
              </ModalHeader>
              <ModalBody>
                <Lede>해당 결제를 환불 처리합니다. 사유를 반드시 기록해 주세요.</Lede>
                <TargetBox>
                  {partnerName} / {labelText} / {Number(refundTarget.amount || 0).toLocaleString()}원
                </TargetBox>
                <Field>
                  <label>환불 사유 <Req>*</Req></label>
                  <textarea
                    rows={4}
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="사유를 입력해 주세요."
                  />
                </Field>
                <Notice>
                  · 환불은 토스페이먼츠 cancel API 를 호출해 실결제 취소합니다.<br />
                  · 카드 결제는 즉시 처리, 계좌이체는 영업일 1~2일 소요됩니다.<br />
                  · 환불 처리 시 연결된 미수금/수수료/배정 기록이 자동으로 되돌려집니다.
                </Notice>
              </ModalBody>
              <ModalFooter>
                <CancelBtn disabled={refundLoading} onClick={() => setRefundTarget(null)}>닫기</CancelBtn>
                <SubmitBtn disabled={refundLoading || !refundReason.trim()} onClick={submitRefund}>
                  {refundLoading ? '처리 중...' : '환불 실행'}
                </SubmitBtn>
              </ModalFooter>
            </Modal>
          </ModalBg>
        );
      })()}
    </Wrap>
  );
};

export default PaymentHistory;

const Wrap = styled.div`
  padding: 24px;
  background: #F6F8FA;
  min-height: 100vh;
`;
const Header = styled.div`
  display: flex; justify-content: space-between; align-items: flex-start;
  background: #fff; border: 1px solid #E5E7EB; border-radius: 12px;
  padding: 20px 24px; margin-bottom: 16px;
`;
const Eyebrow = styled.div`
  font-size: 12px; color: #6B7280; margin-bottom: 4px;
`;
const Title = styled.h1`
  font-size: 22px; font-weight: 700; margin: 0 0 6px; color: #111827;
`;
const Subtitle = styled.p`
  font-size: 13px; color: #6B7280; margin: 0; line-height: 1.6;
`;
const Actions = styled.div` display: flex; gap: 8px; `;
const ToolBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border: 1px solid #E5E7EB; background: #fff;
  border-radius: 8px; font-size: 13px; cursor: pointer;
  &:hover { background: #F9FAFB; }
`;
const KpiRow = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;
const KpiCard = styled.div`
  background: #fff; border: 1px solid #E5E7EB; border-radius: 12px;
  padding: 16px 18px;
`;
const KpiLabel = styled.div` font-size: 12px; color: #6B7280; margin-bottom: 6px; `;
const KpiValue = styled.div` font-size: 20px; font-weight: 700; color: #111827; margin-bottom: 4px; `;
const KpiHint = styled.div` font-size: 11px; color: #9CA3AF; `;

const FilterBar = styled.div`
  display: flex; gap: 8px; align-items: center; margin-bottom: 12px;
  background: #fff; border: 1px solid #E5E7EB; border-radius: 10px; padding: 10px 12px;
`;
const SearchBox = styled.div`
  flex: 1; display: flex; align-items: center; gap: 8px; padding: 0 10px;
  border: 1px solid #E5E7EB; border-radius: 8px; height: 36px;
  input { flex: 1; border: 0; outline: none; font-size: 13px; }
`;
const SearchBtn = styled.button`
  height: 36px; padding: 0 18px; background: #4A6CF7; color: #fff; border: 0;
  border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
`;
const FilterSelect = styled.select`
  height: 36px; padding: 0 28px 0 12px; border: 1px solid #E5E7EB; border-radius: 8px;
  font-size: 13px; background: #fff;
`;
const ResultCount = styled.div` font-size: 12px; color: #6B7280; `;

const TableWrap = styled.div`
  background: #fff; border: 1px solid #E5E7EB; border-radius: 10px; overflow: hidden;
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse;
  thead th {
    background: #F9FAFB; padding: 10px 12px; font-size: 12px; font-weight: 600;
    color: #6B7280; text-align: left; border-bottom: 1px solid #E5E7EB;
  }
  tbody tr { border-bottom: 1px solid #F3F4F6; }
  tbody tr:hover { background: #FAFBFC; }
`;
const Th = styled.th``;
const ThRight = styled.th` text-align: right; `;
const ThRightAlign = styled.th` text-align: right; `;
const Td = styled.td` padding: 12px; font-size: 13px; color: #111827; vertical-align: top; `;
const TdRight = styled(Td)` text-align: right; `;
const TdRightAlign = styled(Td)` text-align: right; `;
const StatusCell = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  a { color: #9CA3AF; display: inline-flex; align-items: center; font-size: 14px;
    &:hover { color: #4A6CF7; }
  }
`;
const RefundedBadge = styled.span`
  display: inline-block; padding: 8px 18px; border-radius: 8px;
  background: #FECACA; color: #DC2626; font-size: 12px; font-weight: 700;
  cursor: not-allowed;
`;
const Empty = styled.div` padding: 60px 0; text-align: center; color: #9CA3AF; font-size: 13px; `;
const LoadingWrap = styled.div` padding: 60px 0; display: flex; justify-content: center; `;

const TargetCell = styled.div`
  display: flex; gap: 8px; align-items: flex-start;
  strong { font-size: 13px; color: #111827; }
  .muted { font-size: 11px; color: #9CA3AF; margin-top: 2px; }
`;
const SmallTag = styled.span`
  display: inline-block; padding: 2px 6px; background: #DBEAFE; color: #1D4ED8;
  border-radius: 4px; font-size: 10px; font-weight: 700; margin-top: 1px;
`;
const OrderId = styled.div`
  display: flex; flex-direction: column;
  .muted { font-size: 11px; color: #9CA3AF; margin-top: 2px; }
`;
const Amount = styled.div`
  display: flex; gap: 8px; align-items: center;
  strong { font-size: 14px; color: #111827; }
`;
const Badge = styled.span`
  display: inline-block; padding: 3px 8px; border-radius: 10px; font-size: 11px; font-weight: 700;
  color: ${(p) => p.$color}; background: ${(p) => p.$bg};
`;
const RefundCell = styled.div`
  .muted { font-size: 11px; color: #9CA3AF; margin-top: 2px; }
`;
const RefundBtn = styled.button`
  padding: 6px 14px; background: #fff; border: 1px solid #EF4444; color: #EF4444;
  border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer;
  &:hover { background: #FEF2F2; }
`;
const DetailBtn = styled.a`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 6px 12px; background: #fff; border: 1px solid #E5E7EB; color: #4B5563;
  border-radius: 6px; font-size: 12px; cursor: pointer; text-decoration: none;
  &:hover { background: #F9FAFB; }
`;

const ModalBg = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 100;
  display: flex; align-items: center; justify-content: center; padding: 16px;
`;
const Modal = styled.div`
  width: 100%; max-width: 480px; background: #fff; border-radius: 14px; overflow: hidden;
  display: flex; flex-direction: column;
`;
const ModalHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #E5E7EB;
  h3 { margin: 0; font-size: 16px; font-weight: 700; }
`;
const CloseBtn = styled.button`
  background: none; border: 0; cursor: pointer; font-size: 20px; color: #6B7280;
`;
const ModalBody = styled.div` padding: 20px; `;
const Field = styled.div`
  margin-bottom: 14px;
  label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 6px; }
  textarea {
    width: 100%; padding: 10px; border: 1px solid #E5E7EB; border-radius: 8px;
    font-size: 13px; resize: vertical; box-sizing: border-box;
    font-family: inherit;
  }
`;
const Req = styled.span` color: #EF4444; `;
const Lede = styled.div`
  font-size: 13px; color: #374151; line-height: 1.6; margin-bottom: 12px;
`;
const TargetBox = styled.div`
  padding: 12px; background: #F3F4F6; border: 1px solid #E5E7EB;
  border-radius: 8px; font-size: 13px; color: #111827; font-weight: 600;
  margin-bottom: 16px;
`;
const Readonly = styled.div`
  padding: 10px; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px;
  font-size: 13px; color: #374151;
`;
const Notice = styled.div`
  padding: 12px; background: #FEF3C7; border-radius: 8px; font-size: 12px;
  color: #92400E; line-height: 1.6;
`;
const ModalFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 8px; padding: 14px 20px;
  border-top: 1px solid #E5E7EB; background: #FAFBFC;
`;
const CancelBtn = styled.button`
  padding: 10px 18px; background: #fff; border: 1px solid #E5E7EB; color: #6B7280;
  border-radius: 8px; font-size: 13px; cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const SubmitBtn = styled.button`
  padding: 10px 22px; background: #EF4444; color: #fff; border: 0;
  border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
